import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { deleteDoc, doc, getDoc, setDoc } from "firebase/firestore";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { auth } from "../../firebase/auth";
import { db } from "../../firebase/firestore";
import { getStorageJSON, isValidEmail, normalizeDeliveryArea, sanitizeText, SERVICEABLE_AREAS } from "../../utils/helpers";
import { getErrorMessage, logError, logInfo, logSuccess, logWarning } from "../../utils/errorHandler";

export default function Settings() {
  const navigate = useNavigate();
  const currentUser = getStorageJSON("user", null);

  const [name, setName] = useState(sanitizeText(currentUser?.name || "", 60));
  const [email, setEmail] = useState((currentUser?.email || "").toLowerCase());
  const [phone, setPhone] = useState(currentUser?.phone || "");
  const [location, setLocation] = useState(normalizeDeliveryArea(currentUser?.location));
  const [address, setAddress] = useState({
    flatNo: currentUser?.addressDetails?.flatNo || "",
    buildingNumber: currentUser?.addressDetails?.buildingNumber || "",
    road: currentUser?.addressDetails?.road || "",
    landmark: currentUser?.addressDetails?.landmark || "",
    pinCode: currentUser?.addressDetails?.pinCode || "",
  });

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [notifyOrders, setNotifyOrders] = useState(currentUser?.preferences?.notifyOrders !== false);
  const [notifyOffers, setNotifyOffers] = useState(currentUser?.preferences?.notifyOffers !== false);
  const [notifyPromo, setNotifyPromo] = useState(currentUser?.preferences?.notifyPromo !== false);
  const [language, setLanguage] = useState(currentUser?.preferences?.language || "en");

  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [status, setStatus] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [settingsError, setSettingsError] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(false);

  useEffect(() => {
    const hydrateFromFirestore = async () => {
      if (!currentUser?.uid) return;

      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (!snap.exists()) {
          logInfo("Settings", "No user profile found in Firestore");
          return;
        }

        const data = snap.data();
        setName(sanitizeText(data.name || currentUser?.name || "", 60));
        setEmail((data.email || currentUser?.email || "").toLowerCase());
        setPhone(data.phone || currentUser?.phone || "");
        setLocation(normalizeDeliveryArea(data.location || currentUser?.location));
        setAddress({
          flatNo: data.addressDetails?.flatNo || "",
          buildingNumber: data.addressDetails?.buildingNumber || "",
          road: data.addressDetails?.road || "",
          landmark: data.addressDetails?.landmark || "",
          pinCode: data.addressDetails?.pinCode || "",
        });
        setNotifyOrders(data.preferences?.notifyOrders !== false);
        setNotifyOffers(data.preferences?.notifyOffers !== false);
        setNotifyPromo(data.preferences?.notifyPromo !== false);
        setLanguage(data.preferences?.language || "en");
        setSettingsError("");
        logSuccess("Settings", "Settings loaded from Firestore");
      } catch (err) {
        logError("Settings Hydrate", err);
        setSettingsError(getErrorMessage(err));
      }
    };

    hydrateFromFirestore();
  }, [currentUser?.email, currentUser?.location, currentUser?.name, currentUser?.phone, currentUser?.uid]);

  const saveSettings = async (event) => {
    event.preventDefault();
    setStatus("");
    setLoadingSettings(true);

    try {
      const cleanedName = sanitizeText(name, 60);
      const cleanedEmail = email.trim().toLowerCase();
      const cleanedPhone = phone.trim();
      const cleanedAddress = {
        flatNo: sanitizeText(address.flatNo, 40),
        buildingNumber: sanitizeText(address.buildingNumber, 60),
        road: sanitizeText(address.road, 80),
        landmark: sanitizeText(address.landmark, 80),
        pinCode: sanitizeText(address.pinCode, 10),
      };

      if (cleanedName.length < 2) {
        logWarning("Settings Validation", "Invalid name entered");
        setStatus("Please enter a valid name.");
        return;
      }

      if (!isValidEmail(cleanedEmail)) {
        logWarning("Settings Validation", "Invalid email entered");
        setStatus("Please enter a valid email.");
        return;
      }

      if (cleanedAddress.pinCode && !/^\d{6}$/.test(cleanedAddress.pinCode)) {
        logWarning("Settings Validation", "Invalid pin code entered");
        setStatus("Please enter a valid 6-digit pin code.");
        return;
      }

      const nextUser = {
        ...(currentUser || {}),
        name: cleanedName,
        email: cleanedEmail,
        phone: cleanedPhone,
        location,
        addressDetails: cleanedAddress,
        preferences: {
          notifyOrders,
          notifyOffers,
          notifyPromo,
          language,
        },
      };

      localStorage.setItem("user", JSON.stringify(nextUser));

      if (currentUser?.uid) {
        await setDoc(
          doc(db, "users", currentUser.uid),
          {
            name: cleanedName,
            email: cleanedEmail,
            phone: cleanedPhone,
            location,
            addressDetails: cleanedAddress,
            address: `${cleanedAddress.flatNo}, ${cleanedAddress.buildingNumber}, ${cleanedAddress.road}, ${cleanedAddress.landmark} - ${cleanedAddress.pinCode}`,
            preferences: {
              notifyOrders,
              notifyOffers,
              notifyPromo,
              language,
            },
            updatedAt: new Date(),
          },
          { merge: true }
        );
      }

      setStatus("Settings saved successfully.");
      setSettingsError("");
      logSuccess("Settings", "Settings saved successfully");
      setTimeout(() => setStatus(""), 3000);
    } catch (err) {
      logError("Settings Save", err);
      const errorMsg = getErrorMessage(err);
      setStatus(`Failed to save settings: ${errorMsg}`);
      setSettingsError(errorMsg);
    } finally {
      setLoadingSettings(false);
    }
  };

  const savePreferences = async (event) => {
    event.preventDefault();
    setStatus("");
    setLoadingSettings(true);

    try {
      const nextUser = {
        ...(currentUser || {}),
        preferences: {
          notifyOrders,
          notifyOffers,
          notifyPromo,
          language,
        },
      };

      localStorage.setItem("user", JSON.stringify(nextUser));

      if (currentUser?.uid) {
        await setDoc(
          doc(db, "users", currentUser.uid),
          {
            preferences: { notifyOrders, notifyOffers, notifyPromo, language },
            updatedAt: new Date(),
          },
          { merge: true }
        );
      }

      setStatus("Preferences saved successfully.");
      setSettingsError("");
      logSuccess("Preferences", "Preferences saved successfully");
      setTimeout(() => setStatus(""), 3000);
    } catch (err) {
      logError("Preferences Save", err);
      const errorMsg = getErrorMessage(err);
      setStatus(`Failed to save preferences: ${errorMsg}`);
      setSettingsError(errorMsg);
    } finally {
      setLoadingSettings(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setStatus("");
    setLoadingSettings(true);

    try {
      if (!currentPassword || !newPassword || !confirmPassword) {
        setStatus("Fill current and new password fields.");
        return;
      }

      if (newPassword.length < 8) {
        setStatus("New password must be at least 8 characters.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setStatus("New passwords do not match.");
        return;
      }

      const currentAuthUser = auth.currentUser;
      if (!currentAuthUser || !currentAuthUser.email) {
        setStatus("Please login again to change password.");
        return;
      }

      const credential = EmailAuthProvider.credential(currentAuthUser.email, currentPassword);
      await reauthenticateWithCredential(currentAuthUser, credential);
      await updatePassword(currentAuthUser, newPassword);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStatus("Password changed successfully.");
      setSettingsError("");
      logSuccess("Password", "Password changed successfully");
      setTimeout(() => setStatus(""), 3000);
    } catch (err) {
      logError("Password Change", err);
      const errorMsg = getErrorMessage(err);
      setStatus(`Could not change password: ${errorMsg}`);
      setSettingsError(errorMsg);
    } finally {
      setLoadingSettings(false);
    }
  };

  const deleteAccount = async () => {
    setStatus("");
    setLoadingSettings(true);

    try {
      if (deleteConfirm !== "DELETE") {
        setStatus('Type DELETE in confirmation box before deleting account.');
        return;
      }

      const currentAuthUser = auth.currentUser;
      if (!currentAuthUser) {
        setStatus("Please login again before deleting account.");
        return;
      }

      if (currentUser?.uid) {
        await deleteDoc(doc(db, "users", currentUser.uid));
      }

      await deleteUser(currentAuthUser);
      localStorage.removeItem("user");
      localStorage.removeItem("cart");
      localStorage.removeItem("wishlist");
      logSuccess("Account", "Account deleted successfully");
      navigate("/login", { replace: true });
    } catch (err) {
      logError("Account Delete", err);
      const errorMsg = getErrorMessage(err);
      setStatus(`Failed to delete account: ${errorMsg}`);
      setSettingsError(errorMsg);
    } finally {
      setLoadingSettings(false);
    }
  };

  const tabs = [
    { id: "profile", label: "👤 Profile", icon: "👤" },
    { id: "preferences", label: "🔔 Preferences", icon: "🔔" },
    { id: "security", label: "🔒 Security", icon: "🔒" },
    { id: "account", label: "⚠️ Account", icon: "⚠️" },
  ];

  return (
    <div className="home">
      <Navbar />

      <div className="customer-page">
        <div className="customer-page-header">
          <h2>Account Settings</h2>
          <p>Manage profile, preferences, security and account options.</p>
        </div>

        <div className="settings-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {status && (
          <p className={`form-status ${status.toLowerCase().includes("success") ? "success" : ""}`}>
            {status}
          </p>
        )}

        {settingsError && <div className="error-banner">⚠️ {settingsError}</div>}
        {loadingSettings && <div className="loading-banner">Updating settings...</div>}

        {activeTab === "profile" && (
          <section className="customer-card settings-section">
            <h3>Profile & Address</h3>
            <form className="customer-form" onSubmit={saveSettings}>
              <label>Full Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required />

              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />

              <label>Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={12}
                placeholder="10-digit number"
              />

              <label>Delivery Area</label>
              <select value={location} onChange={(e) => setLocation(e.target.value)}>
                {SERVICEABLE_AREAS.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>

              <div className="address-grid">
                <div>
                  <label>Flat No.</label>
                  <input
                    type="text"
                    value={address.flatNo}
                    onChange={(e) => setAddress((prev) => ({ ...prev, flatNo: e.target.value }))}
                    placeholder="e.g., 123"
                  />
                </div>
                <div>
                  <label>Building</label>
                  <input
                    type="text"
                    value={address.buildingNumber}
                    onChange={(e) => setAddress((prev) => ({ ...prev, buildingNumber: e.target.value }))}
                    placeholder="e.g., Shreeji Towers"
                  />
                </div>
                <div>
                  <label>Road</label>
                  <input
                    type="text"
                    value={address.road}
                    onChange={(e) => setAddress((prev) => ({ ...prev, road: e.target.value }))}
                    placeholder="e.g., MG Road"
                  />
                </div>
                <div>
                  <label>Landmark</label>
                  <input
                    type="text"
                    value={address.landmark}
                    onChange={(e) => setAddress((prev) => ({ ...prev, landmark: e.target.value }))}
                    placeholder="e.g., Near Station"
                  />
                </div>
                <div>
                  <label>Pin Code</label>
                  <input
                    type="text"
                    value={address.pinCode}
                    onChange={(e) => setAddress((prev) => ({ ...prev, pinCode: e.target.value }))}
                    maxLength={6}
                    placeholder="400050"
                  />
                </div>
              </div>

              <button type="submit" className="primary-action" disabled={loadingSettings}>Save Profile</button>
            </form>
          </section>
        )}

        {activeTab === "preferences" && (
          <section className="customer-card settings-section">
            <h3>Notification & Language Preferences</h3>
            <form className="customer-form" onSubmit={savePreferences}>
              <div className="preference-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={notifyOrders} onChange={(e) => setNotifyOrders(e.target.checked)} />
                  <span>Order Updates</span>
                  <small>Get notified about order status changes</small>
                </label>
              </div>

              <div className="preference-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={notifyOffers} onChange={(e) => setNotifyOffers(e.target.checked)} />
                  <span>Special Offers</span>
                  <small>Receive notifications about new deals and coupons</small>
                </label>
              </div>

              <div className="preference-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={notifyPromo} onChange={(e) => setNotifyPromo(e.target.checked)} />
                  <span>Promotional Content</span>
                  <small>Get news about our special events and campaigns</small>
                </label>
              </div>

              <label>Preferred Language</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="hi">हिंदी (Hindi)</option>
                <option value="gu">ગુજરાતી (Gujarati)</option>
              </select>

              <button type="submit" className="primary-action" disabled={loadingSettings}>Save Preferences</button>
            </form>
          </section>
        )}

        {activeTab === "security" && (
          <section className="customer-card settings-section">
            <h3>Security & Password</h3>
            <form className="customer-form" onSubmit={changePassword}>
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Enter your current password"
              />

              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
              />

              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter new password"
              />

              <button type="submit" className="secondary-action" disabled={loadingSettings}>Change Password</button>
            </form>

            <div className="settings-section" style={{ marginTop: "2rem" }}>
              <h4>Security Tips</h4>
              <ul>
                <li>Use a strong, unique password (min 8 characters)</li>
                <li>Never share your password with anyone</li>
                <li>Change your password periodically</li>
                <li>Log out from untrusted devices</li>
              </ul>
            </div>
          </section>
        )}

        {activeTab === "account" && (
          <section className="customer-card settings-section">
            <h3>Help & Support</h3>
            <div className="settings-actions">
              <button className="primary-action" onClick={() => navigate("/support")}>Open Support Center</button>
              <button className="secondary-action" onClick={() => navigate("/orders")}>My Orders</button>
              <button className="secondary-action" onClick={() => navigate("/about")}>About Us</button>
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h4>Policies</h4>
              <div className="settings-actions">
                <button className="secondary-action" onClick={() => navigate("/terms")}>Terms & Conditions</button>
                <button className="secondary-action" onClick={() => navigate("/privacy")}>Privacy Policy</button>
                <button className="secondary-action" onClick={() => navigate("/refund")}>Refund Policy</button>
              </div>
            </div>

            <div className="danger-zone" style={{ marginTop: "2.5rem" }}>
              <h4>Delete Account</h4>
              <p>This action is permanent and cannot be undone.</p>
              <label>Type DELETE to remove your account permanently</label>
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
              />
              <button className="danger-action" onClick={deleteAccount} disabled={loadingSettings}>Delete Account</button>
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}